import { useEffect, useMemo, useState } from "react";
import { useUrlSearch } from "@/lib/useUrlSearch";
import { api, formatApiError, inr, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, MessageSquare, Mail, Phone, History, Pencil, FolderOpen, Trophy, BellRing, Sparkles, FileText, PackageCheck, Paperclip, Factory, XCircle, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Paged } from "@/components/Pager";
import { ProductCombobox } from "@/components/ProductCombobox";
import { SearchCombobox } from "@/components/SearchCombobox";
import { LeadSizeIntel } from "@/components/LeadSizeIntel";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export function sizeLabel(input, category) {
  if (category === "Cord") {
    const d = (input || "").match(/(\d+(?:\.\d+)?)/);
    return d ? `Diameter ${parseFloat(d[1])}mm` : "";
  }
  const m = (input || "").match(/(\d+(?:\.\d+)?)\s*[xX*×]\s*(\d+(?:\.\d+)?)/);
  if (!m || !category) return "";
  const a = parseFloat(m[1]), b = parseFloat(m[2]);
  if (category === "O'Rings") return `ID ${a}mm x CS ${b}mm`;
  if (category === "Profile" || category === "Strip") return `Width ${a}mm x Height ${b}mm`;
  const t = +(((b - a) / 2).toFixed(2));
  return `ID ${a}mm x OD ${b}mm x Thickness ${t}mm`;
}


const emptyItem = {
  product_id: "", product_code: "", product_name: "", category: "", color: "",
  cut_length: "",
  size_input: "", size_label: "", tc_clamp: false, tc_size_input: "", tc_size_label: "",
  quantity: "", unit: "Meter",
  use_stock: false, stock_qty: "", stock_id: "", _stock: null,
  purchase_rate: "", margin_percent: "", sales_rate: 0, _hint: null,
};

const empty = {
  customer_id: "", company_name: "", contact_person: "", mobile: "", email: "",
  stage: "New", notes: "", internal_note: "", in_house: false, items: [{ ...emptyItem }],
};

const salesRate = (it) => {
  const p = +it.purchase_rate || 0, m = +it.margin_percent || 0;
  return Math.round(p * (1 + m / 100));
};

export default function Leads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [leadSearch, setLeadSearch] = useUrlSearch();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [showInternal, setShowInternal] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [activityOpen, setActivityOpen] = useState(null);
  const [note, setNote] = useState("");
  const [historyFor, setHistoryFor] = useState(null);
  const [aiFor, setAiFor] = useState(null);
  const [aiText, setAiText] = useState("");

  const aiSummary = async (l) => {
    setAiFor(l); setAiText("");
    try {
      const r = await api.post(`/ai/lead-summary/${l.id}`);
      setAiText(r.data.text);
    } catch (e) { setAiText(`Could not generate summary — ${formatApiError(e)}`); }
  };

  const load = () => Promise.all([
    api.get("/leads").then((r) => setLeads(r.data)),
    api.get("/customers").then((r) => setCustomers(r.data)),
    api.get("/products").then((r) => setProducts(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const now = new Date();
  const sortNewest = (arr) => [...arr].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const { active, archive, won, lost } = useMemo(() => {
    const norm = leadSearch.trim().toLowerCase();
    const src = norm
      ? leads.filter((l) => [l.company_name, l.number, l.contact_person, l.mobile]
          .some((v) => String(v || "").toLowerCase().includes(norm)))
      : leads;
    const active = [], archive = [], won = [], lost = [];
    for (const l of src) {
      if (l.won) { won.push(l); continue; }
      if (l.stage === "Lost") { lost.push(l); continue; }
      const d = new Date(l.created_at);
      if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()) active.push(l);
      else archive.push(l);
    }
    return { active: sortNewest(active), archive: sortNewest(archive), won: sortNewest(won), lost: sortNewest(lost) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads, leadSearch]);
  const [leadTab, setLeadTab] = useState("active");
  useEffect(() => {
    if (!leadSearch.trim()) return;
    const buckets = { active, archive, won, lost };
    if ((buckets[leadTab] || []).length === 0) {
      const first = ["active", "archive", "won", "lost"].find((k) => buckets[k].length > 0);
      if (first) setLeadTab(first);
    }
  }, [leadSearch, active.length, archive.length, won.length, lost.length]);

  const archiveTree = useMemo(() => {
    const tree = {};
    for (const l of archive) {
      const d = new Date(l.created_at);
      const y = d.getFullYear(), m = d.getMonth();
      tree[y] = tree[y] || {};
      tree[y][m] = tree[y][m] || [];
      tree[y][m].push(l);
    }
    return tree;
  }, [archive]);

  const setItem = (idx, patch) => {
    setForm((f) => {
      const items = f.items.map((it, i) => {
        if (i !== idx) return it;
        const next = { ...it, ...patch };
        next.size_label = sizeLabel(next.size_input, next.category);
        next.tc_size_label = "";
        if (!f.in_house) next.sales_rate = salesRate(next);
        return next;
      });
      return { ...f, items };
    });
  };

  const fetchHint = async (idx, item) => {
    if (!item.product_id || !item.size_input) return;
    try {
      const r = await api.get("/utils/price-history", { params: { product_id: item.product_id, size: item.size_input, limit: 1 } });
      setItem(idx, { _hint: r.data[0] || false });
    } catch { /* ignore */ }
  };

  const fetchStock = async (idx, item) => {
    const size = item.size_label || item.size_input || "";
    if (!/\d/.test(size)) return;
    try {
      const r = await api.get("/size-intel/stock-match", { params: { size, color: item.color || "" } });
      const match = r.data.match || null;
      setItem(idx, { _stock: match, ...(match ? {} : { use_stock: false, stock_qty: "" }) });
    } catch { /* ignore */ }
  };

  const showHistory = async (item) => {
    try {
      const r = await api.get("/utils/price-history", { params: { product_id: item.product_id, size: item.size_input, limit: 10 } });
      setHistoryFor({ product_name: item.product_name, size: item.size_label || item.size_input, records: r.data });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    if (!form.customer_id) { toast.error("Please select a customer"); return; }
    const items = form.items
      .filter((it) => it.product_id || it.product_name)
      .map(({ _hint, _stock, ...it }) => ({
        ...it,
        quantity: +it.quantity || 0,
        use_stock: !!it.use_stock,
        stock_qty: +it.stock_qty || 0,
        stock_id: it.stock_id || "",
        purchase_rate: form.in_house ? 0 : +it.purchase_rate || 0,
        margin_percent: form.in_house ? 0 : +it.margin_percent || 0,
        sales_rate: form.in_house ? +it.sales_rate || 0 : salesRate(it),
      }));
    try {
      const body = { ...form, items };
      if (!body.follow_up_date) delete body.follow_up_date;
      let resp;
      if (editing) resp = await api.put(`/leads/${editing}`, body);
      else resp = await api.post("/leads", body);
      toast.success("Lead saved"); setOpen(false); setForm(empty); setEditing(null); load();
      const matches = resp?.data?.stock_matches || [];
      if (matches.length) {
        toast.info(`📦 In stock: ${matches.map((m) => `${m.product_name} ${m.size} (${m.quantity} ${m.unit})`).join("; ")}`, { duration: 9000 });
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => { if (!window.confirm("Delete lead?")) return; await api.delete(`/leads/${id}`); load(); };
  const toQuote = async (l) => {
    try {
      const r = await api.post(`/quotations/from-lead/${l.id}`);
      toast.success(`Quotation ${r.data.number} created from ${l.number || "lead"}`);
      navigate("/quotations");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const edit = (l) => {
    setEditing(l.id);
    const items = (l.items?.length ? l.items : [{ ...emptyItem }]).map((it) => ({ ...emptyItem, ...it }));
    setForm({
      ...empty, ...l,
      follow_up_date: l.follow_up_date ? l.follow_up_date.slice(0, 10) : "",
      items,
    });
    setOpen(true);
    items.forEach((it, idx) => fetchStock(idx, it));
  };
  const addActivity = async (lid) => {
    if (!note.trim()) return;
    await api.post(`/leads/${lid}/activity`, { kind: "note", text: note });
    setNote(""); load();
  };

  const setFollowup = async (l) => {
    const cur = l.follow_up_date ? l.follow_up_date.slice(0, 10) : new Date(Date.now() + 86400000).toISOString().slice(0, 10);
    const d = window.prompt("Follow-up date (YYYY-MM-DD) — leave blank to clear:", cur);
    if (d === null) return;
    try {
      await api.patch(`/leads/${l.id}/followup`, null, { params: { date: d.trim() } });
      toast.success(d.trim() ? `Follow-up set for ${d.trim()}` : "Follow-up cleared");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const currentLead = leads.find((l) => l.id === activityOpen);

  const viewPo = async (l) => {
    try {
      const r = await api.get(`/files/${l.po_file_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const wdSince = (iso) => {
    if (!iso) return 0;
    const s = new Date(iso);
    let d = new Date(s.getFullYear(), s.getMonth(), s.getDate());
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let n = 0;
    while (d < end) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0) n++;
    }
    return n;
  };
  const lastTouch = (l) => (l.activities || []).reduce((m, a) => ((a.at || "") > m ? a.at : m), l.created_at || "");

  const markLost = async (l) => {
    const reason = window.prompt(`Mark "${l.company_name || l.number}" as Lost — reason?`, "No response");
    if (reason === null) return;
    try {
      await api.post(`/leads/${l.id}/mark-lost`, { reason });
      toast.success("Lead marked Lost");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const reopenLead = async (l) => {
    try {
      await api.post(`/leads/${l.id}/reopen`);
      toast.success("Lead reopened — back in the pipeline");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const LeadCard = ({ l, idx = 0 }) => (
    <div className={`kanban-card rise-in border rounded-md p-3 ${(l.source || "").startsWith("Portal") ? "portal-lead" : ""} ${l.won ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"}`}
         style={{ animationDelay: `${Math.min(idx, 10) * 55}ms` }}
         data-testid={`lead-card-${l.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {l.live && (
            <span className="relative flex h-2.5 w-2.5 flex-none" title="Live — in production / PO issued" data-testid={`lead-live-${l.id}`}>
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
            </span>
          )}
          <div className="text-sm font-medium text-slate-800 truncate">{l.company_name || l.contact_person}</div>
        </div>
        {l.number && <div className="text-[10px] font-mono text-slate-500 flex-none">{l.number}</div>}
      </div>
      {l.stage === "Lost" && (
        <span className="inline-flex items-center gap-1 mt-1 text-[10px] px-1.5 py-0.5 rounded border border-rose-200 bg-rose-50 text-rose-700 font-medium" title={l.lost_reason ? `Reason: ${l.lost_reason}` : ""} data-testid={`lead-lostbadge-${l.id}`}>
          <XCircle size={10} /> Lost{l.lost_reason ? ` · ${l.lost_reason.slice(0, 30)}` : ""}
        </span>
      )}
      {l.in_house && (
        <span className="inline-flex items-center gap-1 mt-1 text-[10px] px-1.5 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 font-medium" data-testid={`lead-inhouse-${l.id}`}>
          <Factory size={10} /> In-House
        </span>
      )}
      {l.po_ai_check && (
        <span title={l.po_ai_check.summary}
              className={`inline-flex items-center gap-1 mt-1 ml-1 text-[10px] px-1.5 py-0.5 rounded border font-medium ${
                l.po_ai_check.match === true ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : l.po_ai_check.match === false ? "border-rose-200 bg-rose-50 text-rose-700"
                : "border-amber-200 bg-amber-50 text-amber-700"}`}
              data-testid={`lead-pocheck-${l.id}`}>
          {l.po_ai_check.match === true ? "PO ✓ AI verified" : l.po_ai_check.match === false ? "PO ✗ mismatch" : "PO — check manually"}
        </span>
      )}

      <div className="mt-2 space-y-1.5">
        {(l.items || []).map((it, i) => (
          <div key={i} className="bg-white/70 border border-slate-100 rounded px-2 py-1.5 text-[11px] text-slate-600 space-y-0.5" data-testid={`lead-item-${l.id}-${i}`}>
            <div><span className="text-slate-400">Product:</span> <span className="text-slate-800 font-medium">{it.product_name || "-"}</span></div>
            <div><span className="text-slate-400">Colour:</span> {it.color || "-"}</div>
            <div><span className="text-slate-400">Length:</span> {it.cut_length || "-"}</div>
            <div><span className="text-slate-400">Size:</span> {it.size_label || it.size_input || "-"}</div>
            {(it.tc_size_input || it.tc_size_label) && <div><span className="text-slate-400">Notes:</span> {it.tc_size_input || it.tc_size_label}</div>}
            <div><span className="text-slate-400">Qty:</span> {+it.quantity || 0} {it.unit}</div>
          </div>
        ))}
        {!l.items?.length && <div className="text-[11px] text-slate-400">No products</div>}
      </div>

      <div className="flex items-center justify-between mt-2">
        <div className="text-xs font-mono text-slate-700 tabular-nums"><span className="text-slate-400 font-sans">Total: </span>{inr(l.value)}</div>
        <div className="flex items-center gap-1.5">
          {l.mobile && <a href={`tel:${l.mobile}`} className="text-slate-400 hover:text-blue-600"><Phone size={12} /></a>}
          {l.email && <a href={`mailto:${l.email}`} className="text-slate-400 hover:text-blue-600"><Mail size={12} /></a>}
          {l.mobile && <a href={`https://wa.me/${l.mobile.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-emerald-600"><MessageSquare size={12} /></a>}
        </div>
      </div>
      <div className="text-[10px] text-slate-400 mt-1">{fmtDate(l.created_at)}{l.won && l.won_at ? ` · Won ${fmtDate(l.won_at)}` : ""}</div>
      {l.po_file_id && (
        <button onClick={() => viewPo(l)} data-testid={`lead-view-po-${l.id}`}
                className="mt-1.5 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 transition-colors duration-200">
          <Paperclip size={10} /> View PO
        </button>
      )}
      {!l.won && (
        <button onClick={() => setFollowup(l)} data-testid={`lead-fu-${l.id}`}
                className={`mt-1.5 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border transition-colors duration-200 ${
                  l.follow_up_date
                    ? (l.follow_up_date.slice(0, 10) < new Date().toISOString().slice(0, 10)
                        ? "border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100"
                        : "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100")
                    : "border-slate-200 text-slate-400 hover:text-amber-700 hover:border-amber-200"
                }`}>
          <BellRing size={10} /> {l.follow_up_date ? `Follow-up: ${fmtDate(l.follow_up_date)}` : "Set follow-up"}
        </button>
      )}
      <div className="flex gap-1 mt-2 items-center">
        <button onClick={() => edit(l)} className="p-1.5 text-slate-500 hover:text-[#2563EB] rounded hover:bg-slate-100 transition-colors duration-150" title="Edit" data-testid={`lead-edit-${l.id}`}><Pencil size={13} /></button>
        {!l.won && l.customer_id && (l.items || []).some((it) => it.product_name || it.product_id) && (
          <button onClick={() => toQuote(l)} className="p-1.5 text-emerald-600 hover:text-emerald-800 rounded hover:bg-emerald-50 transition-colors duration-150" title="Convert to Quotation" data-testid={`lead-quote-${l.id}`}><FileText size={13} /></button>
        )}
        <button onClick={() => setActivityOpen(l.id)} className="p-1.5 text-slate-500 hover:text-[#2563EB] rounded hover:bg-slate-100 transition-colors duration-150" title="Timeline" data-testid={`lead-timeline-${l.id}`}><History size={13} /></button>
        {!l.won && ["New", "Contacted", "Follow-up"].includes(l.stage) && wdSince(lastTouch(l)) >= 12 && (
          <button onClick={() => markLost(l)} className="p-1.5 text-rose-600 hover:text-rose-800 rounded hover:bg-rose-50 transition-colors duration-150" title={`Mark Lost — ${wdSince(lastTouch(l))} working days without response`} data-testid={`lead-lost-${l.id}`}><XCircle size={13} /></button>
        )}
        {l.stage === "Lost" && (
          <button onClick={() => reopenLead(l)} className="p-1.5 text-emerald-600 hover:text-emerald-800 rounded hover:bg-emerald-50 transition-colors duration-150" title="Reopen lead" data-testid={`lead-reopen-${l.id}`}><RotateCcw size={13} /></button>
        )}
        <button onClick={() => aiSummary(l)} className="p-1.5 text-indigo-500 hover:text-indigo-700 rounded hover:bg-indigo-50 transition-colors duration-150" title="AI summary" data-testid={`lead-ai-${l.id}`}><Sparkles size={13} /></button>
        <button onClick={() => del(l.id)} className="p-1.5 text-slate-500 hover:text-rose-600 rounded hover:bg-rose-50 transition-colors duration-150 ml-auto" title="Delete" data-testid={`lead-del-${l.id}`}><Trash2 size={13} /></button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="leads-page">
      <PageHeader title="Lead Pipeline" subtitle="Active this month · auto-archived monthly · auto-won on invoice"
        right={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditing(null); setShowInternal(false); setForm({ ...empty, items: [{ ...emptyItem }] }); setOpen(true); }}
                      className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="lead-new">
                <Plus size={14} className="mr-1" /> New Lead
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[96vw] sm:max-w-[96vw] h-[94vh] max-h-[94vh] overflow-y-auto content-start lg:left-[calc(16rem+10px)] lg:translate-x-0 lg:top-[10px] lg:translate-y-0 lg:w-[calc(100vw-16rem-20px)] lg:max-w-[calc(100vw-16rem-20px)] lg:h-[calc(100vh-20px)] lg:max-h-[calc(100vh-20px)] lg:rounded-xl shadow-2xl dialog-slide-right">
              <DialogHeader><DialogTitle>{editing ? "Edit Lead" : "New Lead"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Customer *</Label>
                  <SearchCombobox testId="l-customer" placeholder="Select customer" searchPlaceholder="Search customers…"
                    emptyText="No customers yet — create one first"
                    label={customers.find((c) => c.id === form.customer_id)?.company_name || form.company_name}
                    options={customers.map((c) => ({ id: c.id, label: c.company_name, sub: c.city || "" }))}
                    onSelect={(v) => {
                      const c = customers.find((x) => x.id === v);
                      setForm({ ...form, customer_id: v,
                                company_name: c?.company_name || "",
                                contact_person: c?.contact_person || "",
                                mobile: c?.mobile || "", email: c?.email || "" });
                    }} />
                </div>
                <div className="col-span-2 flex flex-col justify-end">
                  <label className={`flex items-center gap-2 text-xs cursor-pointer border rounded-md px-3 h-9 w-fit transition-colors duration-200 ${form.in_house ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600 hover:border-indigo-200"}`}>
                    <Checkbox checked={!!form.in_house} onCheckedChange={(v) => setForm({ ...form, in_house: !!v })} data-testid="l-inhouse-check" />
                    <Factory size={13} />
                    <span className="font-medium uppercase tracking-wider">In-House Production</span>
                  </label>
                  {form.in_house && (
                    <p className="text-[11px] text-indigo-600 mt-1" data-testid="l-inhouse-hint">
                      No vendor / purchase cost — full sales value is profit. A Production Job Card is auto-created when the order is confirmed.
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Products Required</p>
                {form.items.map((it, idx) => (
                  <div key={idx} className="border border-slate-200 rounded-md p-3 space-y-2" data-testid={`l-item-${idx}`}>
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-4">
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Product</Label>
                        {!it.product_id && it.product_name ? (
                          <Input value={it.product_name} onChange={(e) => setItem(idx, { product_name: e.target.value })}
                                 className="mt-1 h-9" data-testid={`l-item-${idx}-product-name`} />
                        ) : (
                        <ProductCombobox products={products} label={it.product_name} testId={`l-item-${idx}-product`}
                          onSelect={(p) => {
                            setItem(idx, { product_id: p.id, product_code: p.product_code || "",
                                           product_name: p.description || "", category: p.category || "",
                                           unit: p.unit || "Meter", _hint: null });
                            fetchHint(idx, { ...it, product_id: p.id });
                          }} />
                        )}
                      </div>
                      <div className="col-span-2">
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Category</Label>
                        <Input value={it.category} readOnly className="mt-1 h-9 bg-slate-50 text-slate-600" data-testid={`l-item-${idx}-category`} />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Color</Label>
                        <Input value={it.color} onChange={(e) => setItem(idx, { color: e.target.value })} onBlur={() => fetchStock(idx, it)} className="mt-1 h-9" data-testid={`l-item-${idx}-color`} />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">{it.category === "Cord" ? "Diameter (e.g. 6)" : "Size (e.g. 8 x 12)"}</Label>
                        <Input value={it.size_input}
                               onChange={(e) => setItem(idx, { size_input: e.target.value })}
                               onBlur={() => { fetchHint(idx, it); fetchStock(idx, it); }}
                               className="mt-1 h-9" data-testid={`l-item-${idx}-size`} />
                        {it.size_label && <p className="text-[11px] text-blue-700 mt-1" data-testid={`l-item-${idx}-sizelabel`}>{it.size_label}</p>}
                      </div>
                    </div>
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-2">
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Qty ({it.unit})</Label>
                        <Input type="number" value={it.quantity} onChange={(e) => setItem(idx, { quantity: e.target.value })} className="mt-1 h-9" data-testid={`l-item-${idx}-qty`} />
                      </div>
                      <div className="col-span-4">
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Length (cut)</Label>
                        <Input value={it.cut_length || ""} onChange={(e) => setItem(idx, { cut_length: e.target.value })}
                               placeholder="e.g. 8 Mtr x 5 Pcs" className="mt-1 h-9" data-testid={`l-item-${idx}-length`} />
                      </div>
                      <div className="col-span-6 flex items-center gap-3 flex-wrap self-end h-9">
                        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                          <Checkbox checked={it.tc_clamp} onCheckedChange={(v) => setItem(idx, { tc_clamp: !!v, ...(v ? {} : { tc_size_input: "" }) })}
                                    data-testid={`l-item-${idx}-notes-check`} />
                          Notes
                        </label>
                        {it.tc_clamp && (
                          <Input value={it.tc_size_input} onChange={(e) => setItem(idx, { tc_size_input: e.target.value })}
                                 placeholder="Write your note…" className="h-8 flex-1 min-w-[200px] text-xs"
                                 data-testid={`l-item-${idx}-notes`} />
                        )}
                      </div>
                    </div>
                    {it._stock && (
                      <div className="flex flex-wrap items-center gap-3 border border-emerald-200 bg-emerald-50 rounded-md px-3 py-2" data-testid={`l-item-${idx}-stock`}>
                        <PackageCheck size={14} className="text-emerald-600 shrink-0" />
                        <span className="text-xs text-emerald-800 font-medium">
                          In stock: {it._stock.quantity} {it._stock.unit} — {it._stock.product_name}{it._stock.color ? ` · ${it._stock.color}` : ""}
                        </span>
                        <label className="flex items-center gap-1.5 text-xs text-emerald-900 cursor-pointer">
                          <Checkbox checked={!!it.use_stock}
                                    onCheckedChange={(v) => setItem(idx, { use_stock: !!v, stock_id: it._stock.id,
                                      stock_qty: v ? Math.min(+it._stock.quantity, +it.quantity || +it._stock.quantity) : "" })}
                                    data-testid={`l-item-${idx}-usestock`} />
                          Use from stock
                        </label>
                        {it.use_stock && (
                          <span className="flex items-center gap-1.5 text-xs text-emerald-800">
                            <Input type="number" value={it.stock_qty}
                                   onChange={(e) => setItem(idx, { stock_qty: Math.min(+e.target.value || 0, +it._stock.quantity) })}
                                   className="h-7 w-24 text-xs bg-white" data-testid={`l-item-${idx}-stockqty`} />
                            from stock → vendor PO gets {Math.max(0, (+it.quantity || 0) - (+it.stock_qty || 0))} {it.unit}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="grid grid-cols-12 gap-2 items-start">
                      {form.in_house ? (
                        <>
                          <div className="col-span-3">
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Sales Rate (₹/{it.unit})</Label>
                            <Input type="number" value={it.sales_rate} onChange={(e) => setItem(idx, { sales_rate: e.target.value })}
                                   className="mt-1 h-9 font-medium" data-testid={`l-item-${idx}-sales`} />
                          </div>
                          <div className="col-span-3">
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Profit (₹) — full sales value</Label>
                            <Input value={(() => { const s = +it.sales_rate || 0, q = +it.quantity || 0; return s && q ? (s * q).toFixed(2) : ""; })()}
                                   readOnly className="mt-1 h-9 bg-emerald-50/60 border-emerald-200 font-medium text-emerald-700" data-testid={`l-item-${idx}-profit`} />
                          </div>
                          <div className="col-span-4 self-end flex items-center h-9 text-[11px] text-indigo-600">
                            <Factory size={12} className="mr-1.5 flex-none" /> In-house — no purchase rate
                          </div>
                        </>
                      ) : (
                        <>
                      <div className="col-span-3">
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Purchase Rate (₹)</Label>
                        <Input type="number" value={it.purchase_rate} onChange={(e) => setItem(idx, { purchase_rate: e.target.value })} className="mt-1 h-9" data-testid={`l-item-${idx}-purchase`} />
                        {it._hint && (
                          <p className="text-[11px] text-amber-700 mt-1" data-testid={`l-item-${idx}-hint`}>
                            Approx cost: ₹{it._hint.purchase_rate} <span className="text-slate-400">({fmtDate(it._hint.created_at)})</span>
                          </p>
                        )}
                      </div>
                      <div className="col-span-2">
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Margin %</Label>
                        <Input type="number" value={it.margin_percent} onChange={(e) => setItem(idx, { margin_percent: e.target.value })} className="mt-1 h-9" data-testid={`l-item-${idx}-margin`} />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Sales Rate (₹/{it.unit})</Label>
                        <Input value={it.sales_rate} readOnly className="mt-1 h-9 bg-slate-50 font-medium text-slate-800" data-testid={`l-item-${idx}-sales`} />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Profit (₹)</Label>
                        <Input value={(() => { const s = +salesRate(it) || 0, p = +it.purchase_rate || 0, q = +it.quantity || 0; return s && p ? ((s - p) * q).toFixed(2) : ""; })()}
                               readOnly className="mt-1 h-9 bg-emerald-50/60 border-emerald-200 font-medium text-emerald-700" data-testid={`l-item-${idx}-profit`} />
                      </div>
                        </>
                      )}
                      <div className="col-span-2 flex gap-1 pt-[19px]">
                        <Button type="button" variant="outline" size="sm" className="h-9 px-2" disabled={!it.product_id}
                                onClick={() => showHistory(it)} title="Price history" data-testid={`l-item-${idx}-history`}>
                          <History size={14} />
                        </Button>
                        <Button type="button" variant="outline" size="sm" className="h-9 px-2 text-rose-600"
                                onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))}
                                disabled={form.items.length === 1} data-testid={`l-item-${idx}-remove`}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                <LeadSizeIntel items={form.items} />
                <Button type="button" variant="outline" size="sm"
                        onClick={() => setForm((f) => ({ ...f, items: [...f.items, { ...emptyItem }] }))} data-testid="l-add-row">
                  <Plus size={13} className="mr-1" /> Add Row
                </Button>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1.5" />
              </div>

              <div>
                <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer w-fit">
                  <Checkbox checked={showInternal || !!form.internal_note}
                            onCheckedChange={(v) => { setShowInternal(!!v); if (!v) setForm({ ...form, internal_note: "" }); }}
                            data-testid="l-internal-check" />
                  <span className="uppercase tracking-widest text-slate-500 text-xs font-medium">Internal Notepad</span>
                  <span className="text-[10px] text-amber-600">(private — never shown to client)</span>
                </label>
                {(showInternal || !!form.internal_note) && (
                  <Textarea value={form.internal_note} onChange={(e) => setForm({ ...form, internal_note: e.target.value })}
                            rows={2} placeholder="e.g. 2% commission payable to Mr. X on this order"
                            className="mt-1.5 bg-amber-50/60 border-amber-200 focus-visible:ring-amber-300" data-testid="l-internal-note" />
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="l-save">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <Tabs value={leadTab} onValueChange={setLeadTab}>
        <div className="flex flex-wrap items-center gap-3">
        <TabsList>
          <TabsTrigger value="active" data-testid="ltab-active">Active Leads ({active.length})</TabsTrigger>
          <TabsTrigger value="archive" data-testid="ltab-archive">Lead Archive ({archive.length})</TabsTrigger>
          <TabsTrigger value="won" data-testid="ltab-won">Won Leads ({won.length})</TabsTrigger>
          <TabsTrigger value="lost" data-testid="ltab-lost">Lost ({lost.length})</TabsTrigger>
        </TabsList>
        <Input value={leadSearch} onChange={(e) => setLeadSearch(e.target.value)} placeholder="Search company, lead no., contact…"
               className="h-9 w-64 bg-white" data-testid="lead-search" />
        </div>

        <TabsContent value="active">
          <Paged items={active}>
            {(pageRows, pagerEl) => (<>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="active-leads">
                {pageRows.map((l, i) => <LeadCard key={l.id} l={l} idx={i} />)}
              </div>
              <div className="mt-2 border border-slate-200 rounded-md overflow-hidden empty:hidden">{pagerEl}</div>
            </>)}
          </Paged>
          {active.length === 0 && <div className="text-center py-14 text-sm text-slate-500 border border-dashed border-slate-200 rounded-md">No active leads this month — click "New Lead" to add one.</div>}
        </TabsContent>

        <TabsContent value="archive">
          <div className="space-y-3" data-testid="lead-archive">
            {Object.keys(archiveTree).sort((a, b) => b - a).map((y) => (
              <div key={y}>
                <div className="text-sm font-bold text-slate-700 uppercase tracking-widest mb-2 flex items-center gap-2"><FolderOpen size={15} className="text-amber-600" /> {y}</div>
                <div className="space-y-2 ml-1">
                  {Object.keys(archiveTree[y]).sort((a, b) => b - a).map((m) => (
                    <details key={m} className="border border-slate-200 rounded-md bg-white" open data-testid={`archive-${y}-${m}`}>
                      <summary className="px-4 py-2.5 text-xs uppercase tracking-wider text-slate-600 font-semibold cursor-pointer flex items-center gap-2 select-none">
                        <FolderOpen size={13} className="text-amber-500" /> {MONTHS[m]} {y}
                        <span className="text-slate-400 normal-case font-normal">· {archiveTree[y][m].length} lead{archiveTree[y][m].length > 1 ? "s" : ""}</span>
                      </summary>
                      <div className="p-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 border-t border-slate-100">
                        {archiveTree[y][m].map((l) => <LeadCard key={l.id} l={l} />)}
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            ))}
            {archive.length === 0 && <div className="text-center py-14 text-sm text-slate-500 border border-dashed border-slate-200 rounded-md">Archive is empty — leads from previous months will appear here automatically on the 1st of each month.</div>}
          </div>
        </TabsContent>

        <TabsContent value="won">
          <div data-testid="won-leads">
            {won.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-emerald-700 mb-3"><Trophy size={15} /> {won.length} lead{won.length > 1 ? "s" : ""} converted to invoice</div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {won.map((l, i) => <LeadCard key={l.id} l={l} idx={i} />)}
            </div>
            {won.length === 0 && <div className="text-center py-14 text-sm text-slate-500 border border-dashed border-slate-200 rounded-md">No won leads yet — a lead moves here automatically once it's converted into an invoice.</div>}
          </div>
        </TabsContent>

        <TabsContent value="lost">
          <div data-testid="lost-leads">
            {lost.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-rose-700 mb-3"><XCircle size={15} /> {lost.length} lost lead{lost.length > 1 ? "s" : ""} — use Reopen to bring one back into the pipeline</div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {lost.map((l, i) => <LeadCard key={l.id} l={l} idx={i} />)}
            </div>
            {lost.length === 0 && <div className="text-center py-14 text-sm text-slate-500 border border-dashed border-slate-200 rounded-md">No lost leads. Leads silent for 12+ working days can be marked Lost from the dashboard follow-up list.</div>}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!historyFor} onOpenChange={(v) => !v && setHistoryFor(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Price History</DialogTitle></DialogHeader>
          <div className="text-sm">
            <p className="text-slate-600 mb-2">{historyFor?.product_name}{historyFor?.size ? ` — ${historyFor.size}` : ""}</p>
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Size</th>
                    <th className="text-left px-3 py-2">Color</th>
                    <th className="text-right px-3 py-2">Qty</th>
                    <th className="text-right px-3 py-2">Purchase ₹</th>
                    <th className="text-right px-3 py-2">Margin %</th>
                    <th className="text-right px-3 py-2">Sales ₹</th>
                  </tr>
                </thead>
                <tbody>
                  {historyFor?.records?.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <td className="px-3 py-2">{fmtDate(r.created_at)}</td>
                      <td className="px-3 py-2">{r.size_label}</td>
                      <td className="px-3 py-2">{r.color || "-"}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.quantity} {r.unit}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.purchase_rate}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.margin_percent}%</td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">{r.sales_rate}</td>
                    </tr>
                  ))}
                  {!historyFor?.records?.length && (
                    <tr><td colSpan={7} className="text-center text-slate-500 py-6">No past records for this product/size.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!activityOpen} onOpenChange={(v) => !v && setActivityOpen(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Activity Timeline</DialogTitle></DialogHeader>
          <div className="text-sm">
            <div className="font-medium mb-2">{currentLead?.company_name}</div>
            <div className="space-y-2 max-h-64 overflow-y-auto scroll-thin border-l-2 border-slate-200 pl-3">
              {currentLead?.activities?.slice().reverse().map((a) => (
                <div key={a.id} className="text-xs">
                  <div className="text-slate-800">{a.text}</div>
                  <div className="text-slate-500">{fmtDate(a.at)} · {a.user}</div>
                </div>
              ))}
              {!currentLead?.activities?.length && <div className="text-slate-500 text-sm">No activity yet.</div>}
            </div>
            <div className="mt-3 flex gap-2">
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" data-testid="l-note" />
              <Button onClick={() => addActivity(activityOpen)} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white">Add</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!aiFor} onOpenChange={(v) => !v && setAiFor(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles size={15} className="text-indigo-500" /> AI Summary — {aiFor?.company_name}</DialogTitle>
          </DialogHeader>
          {aiText ? (
            <div className="text-[13px] text-slate-700 whitespace-pre-wrap leading-relaxed max-h-[60vh] overflow-y-auto" data-testid="lead-ai-text">{aiText}</div>
          ) : (
            <div className="text-sm text-slate-500 py-6 text-center animate-pulse">Analyzing lead…</div>
          )}
          {aiText && (
            <Button variant="outline" size="sm" className="w-fit" data-testid="lead-ai-copy"
                    onClick={() => { navigator.clipboard.writeText(aiText); toast.success("Copied"); }}>Copy</Button>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
